import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient, useCurrentUser, cn } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'
import { useCoinBalance } from '../../_lib/wallet-api'

const MIN_COIN = 1_000_000

type Method = 'alipay' | 'wechat' | 'bank' | 'other'

type InitResp = { withdraw_id: string; expires_at: string }

export function WithdrawPage() {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const { data: wallet } = useCoinBalance(user?.user_id)

  const [amount, setAmount] = useState<number>(MIN_COIN)
  const [method, setMethod] = useState<Method>('alipay')
  const [payee, setPayee] = useState('')
  const [code, setCode] = useState('')
  const [withdrawId, setWithdrawId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [codeBusy, setCodeBusy] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const onInit = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiClient.post<InitResp>('/gapi/user/withdrawals/init', {
        amount_coin: amount,
        payee_info: `${method}: ${payee}`,
      })
      setWithdrawId(r.withdraw_id)
      setResendIn(60)
    } catch (e) {
      setError(e instanceof Error ? e.message : '发起失败')
    } finally {
      setBusy(false)
    }
  }

  const onSendCode = async () => {
    if (!withdrawId) {
      await onInit()
      return
    }
    setCodeBusy(true)
    try {
      // 2026-05-20 Phase 24:server 已实现 /gapi/user/withdrawals/resend-code(withdraw_handlers.go::ResendCode);
      // 仅 status='pending' 允许重发;限速由 SendVerifyCode 内部 60s 冷却保证。
      await apiClient.post('/gapi/user/withdrawals/resend-code', { withdraw_id: withdrawId })
      setResendIn(60)
    } catch (e) {
      setError(e instanceof Error ? e.message : '发送失败')
    } finally {
      setCodeBusy(false)
    }
  }

  const onConfirm = async () => {
    if (!withdrawId) {
      await onInit()
      return
    }
    setBusy(true)
    setError(null)
    try {
      await apiClient.post('/gapi/user/withdrawals/confirm', { withdraw_id: withdrawId, code })
      setDone(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : '确认失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <TopBar
          title="提现"
          right={
            <button type="button" onClick={() => navigate('/wallet/withdraw/records')} className="text-[13px] text-[#19caad]">
              提现记录
            </button>
          }
        />

        {done ? (
          <main data-testid="07hVE" className="flex flex-1 flex-col items-center gap-4 px-6 pt-12">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF7E6] text-[#F59E0B]">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            </span>
            <p className="text-[18px] font-semibold text-[#1A1A1A]">提现申请已提交</p>
            <p className="text-center text-[13px] text-[#9CA3AF]">等待审核，可在「提现记录」查看状态</p>
            <button onClick={() => navigate('/wallet')} className="mt-4 h-11 w-full max-w-xs rounded-lg bg-[#19caad] text-[14px] font-semibold text-white">
              返回钱包
            </button>
          </main>
        ) : (
          <main data-testid="07hVE" className="flex flex-1 flex-col gap-4 px-6 pt-3 pb-32">
            <section className="flex items-center justify-between rounded-xl bg-white px-4 py-3">
              <div>
                <p className="text-[11px] text-[#9CA3AF]">COIN 余额</p>
                <p className="mt-1 flex items-baseline gap-1 text-[16px] font-bold tabular-nums text-[#1A1A1A]">
                  {wallet?.balance ? Number(wallet.balance).toLocaleString() : '0'}
                  <span className="text-[10px] font-normal text-[#9CA3AF]">金币</span>
                  <span className="text-[10px] font-normal text-[#9CA3AF]">≈ ¥{wallet?.balance ? (Number(wallet.balance) / 100).toFixed(2) : '0.00'}</span>
                </p>
              </div>
              <button
                onClick={() => setAmount(Number(wallet?.balance ?? 0))}
                className="rounded-lg bg-[#19caad] px-3 py-1 text-[12px] font-semibold text-white"
              >
                全部
              </button>
            </section>

            <section>
              <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">提现金额</p>
              <div className="flex items-center gap-2 rounded-xl bg-white px-4 py-3">
                <span className="text-[12px] text-[#9CA3AF]">≥</span>
                <input
                  type="number"
                  min={MIN_COIN}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="flex-1 bg-transparent text-[24px] font-bold tabular-nums outline-none"
                />
                <span className="text-[12px] text-[#9CA3AF]">金币</span>
              </div>
              <p className="mt-1 text-[11px] text-[#9CA3AF]">最低提现 {MIN_COIN.toLocaleString()} 金币（≈ ¥{(MIN_COIN / 100).toLocaleString()}）</p>
            </section>

            <section>
              <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">收款方式</p>
              <div className="grid grid-cols-2 gap-2">
                {(['alipay', 'wechat', 'bank', 'other'] as Method[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    className={cn(
                      'flex h-12 items-center gap-2 rounded-xl px-4 text-left text-[13px]',
                      method === m ? 'border-2 border-[#19caad] bg-white' : 'border border-transparent bg-white text-[#1A1A1A]',
                    )}
                  >
                    <span
                      className="flex h-4 w-4 items-center justify-center rounded-full border-2"
                      style={{ borderColor: method === m ? '#19caad' : '#D1D5DB', background: method === m ? '#19caad' : 'transparent' }}
                    />
                    {m === 'alipay' ? '支付宝' : m === 'wechat' ? '微信' : m === 'bank' ? '银行卡' : '其他'}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">收款联系方式</p>
              <textarea
                value={payee}
                onChange={(e) => setPayee(e.target.value)}
                rows={3}
                placeholder="填写支付宝账号 / 微信号 / 银行卡号 + 户名 + 银行 / 邮箱等便于客服联系"
                className="w-full resize-none rounded-xl bg-white px-4 py-3 text-[13px] outline-none placeholder:text-[#B4B7BC]"
                maxLength={300}
              />
            </section>

            <section>
              <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">邮箱验证码</p>
              <div className="flex gap-2">
                <div className="flex h-12 flex-1 gap-1.5 rounded-xl bg-white px-3">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <input
                      key={i}
                      value={code[i] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(-1)
                        const next = code.split('')
                        next[i] = v
                        setCode(next.join(''))
                      }}
                      maxLength={1}
                      inputMode="numeric"
                      className="my-auto h-9 w-7 rounded-md bg-[#F6F7F8] text-center text-[14px] outline-none"
                    />
                  ))}
                </div>
              </div>
              <button
                type="button"
                disabled={codeBusy || resendIn > 0 || !payee || amount < MIN_COIN}
                onClick={onSendCode}
                className="mt-2 h-9 rounded-lg bg-[#19caad] px-4 text-[12px] font-semibold text-white disabled:opacity-50"
              >
                {codeBusy ? '发送中…' : resendIn > 0 ? `${resendIn}s 后重发` : withdrawId ? '重新发送' : '发送验证码'}
              </button>
              <p className="mt-1 text-[10px] text-[#9CA3AF]">已发送至您的注册邮箱 u***@example.com</p>
            </section>

            {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}
          </main>
        )}

        {!done && (
          <div
            className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-[402px] bg-[#F6F7F8] px-6 pt-3"
            style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
          >
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy || amount < MIN_COIN || !payee || code.length !== 6}
              className="h-12 w-full rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
            >
              {busy ? '提交中…' : '提交提现申请'}
            </button>
            <p className="mt-1 text-center text-[10px] text-[#9CA3AF]">提现需经管理员审核 + 人工到账，预计 1~3 个工作日；不收手续费</p>
          </div>
        )}
      </MobileScreen>
    </AppLayout>
  )
}
